import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FaceProcessor from '../components/FaceProcessor.jsx';
import FaceIdentityScanOverlay from '../../face-identity/components/FaceIdentityScanOverlay';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useVoiceGuide } from '../hooks/useVoiceGuide';
import { calculateFaceGuidance, calculateGuideBounds } from '../utils/faceGuidanceLogic';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import {
  persistFaceCapture,
  persistFacePending,
  persistFaceProfileSnapshot,
  recognizeSalonFace,
  warmSalonFaceEngine,
} from '../../face-identity/utils/faceIdentityApi';
import { captureIdentityFaceBlob } from '../../face-identity/utils/captureIdentityFace';
import { SALON_HUB_PATH } from '../config/salonApp';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import { getSalonPendingCheckinId, setSalonEntrySource } from '../utils/salonFaceSession';
import { linkPendingCheckinIfNeeded } from '../utils/visitHandoff';
import CameraPrepPlaceholder from '../../shared/CameraPrepPlaceholder';
import {
  describeVideoInputs,
  listMicroscopeVideoInputs,
  hasCameraPermissionGranted,
  openBuiltinFaceCameraStream,
  pickBuiltinFrontCamera,
  requiresCameraUserGesture,
} from '../../shared/usbMicroscopeCamera';
import { stopNativeScalpInlinePreview } from '../../shared/nyafazNativeBridge';
import '../../shared/camera-capture-shell.css';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './FaceDetectionPage.css';
import '../../face-identity/pages/FaceIdentityPage.css';

const AUTO_CAPTURE_DELAY_MS = 800;
const GRACE_PERIOD_MS = 650;
const LIGHTING_CHECK_THROTTLE_MS = 1500;
const RECOGNIZE_MAX_SIDE = 720;
const RECOGNIZE_JPEG_QUALITY = 0.8;

const SalonScanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const deviceInfo = location.state?.deviceInfo || null;
  const token = location.state?.token || null;
  const nextPath = location.state?.next || SALON_HUB_PATH;
  const mode = 'salon';
  const pendingCheckinId =
    location.state?.pendingCheckinId || getSalonPendingCheckinId() || null;
  const pendingCheckinCode = location.state?.pendingCheckinCode || null;
  const preselectedServices = useMemo(
    () => location.state?.preselectedServices || [],
    [location.state?.preselectedServices],
  );

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceDetectedRef = useRef(false);
  const lightingStatusRef = useRef('checking');
  const lightingMessageRef = useRef('');
  const lastBackendLightingCheckRef = useRef(0);
  const perfectAlignmentStartTimeRef = useRef(null);
  const perfectAlignmentTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const gracePeriodTimerRef = useRef(null);
  const hasAutoCapturedRef = useRef(false);
  const handleCaptureRef = useRef(null);
  const handingOffRef = useRef(false);
  const speechUnlockedRef = useRef(false);
  const latestLandmarksRef = useRef(null);

  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [cameraAwaitingTap, setCameraAwaitingTap] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [faceGuidance, setFaceGuidance] = useState({
    isInsideFrame: false,
    statusColor: 'red',
    message: 'Position your face',
    isFaceStraight: false,
    isFaceNear: false,
  });
  const [lightingStatus, setLightingStatus] = useState('checking');
  const [lightingMessage, setLightingMessage] = useState('');
  const [videoSize, setVideoSize] = useState({ width: 640, height: 480 });
  const [voiceGuideEnabled, setVoiceGuideEnabled] = useState(true);
  const [showVoiceHint, setShowVoiceHint] = useState(false);

  const lightingPass = lightingStatus === 'good' || lightingStatus === 'moderate';
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const { unlockForIOS } = useVoiceGuide(
    'front',
    faceGuidance.statusColor === 'green',
    isCapturing || isIdentifying,
    voiceGuideEnabled && previewEnabled && !isIdentifying,
    lightingPass,
    lightingMessage || '',
    faceGuidance.message || '',
    lightingStatus !== 'checking',
  );

  const clearAlignmentTimers = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (perfectAlignmentTimerRef.current) {
      clearTimeout(perfectAlignmentTimerRef.current);
      perfectAlignmentTimerRef.current = null;
    }
    if (gracePeriodTimerRef.current) {
      clearTimeout(gracePeriodTimerRef.current);
      gracePeriodTimerRef.current = null;
    }
    perfectAlignmentStartTimeRef.current = null;
    setCountdown(null);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setPreviewEnabled(false);
  }, []);

  const checkLighting = useCallback((canvasEl) => {
    if (!canvasEl) return;
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      setLightingStatus('good');
      setLightingMessage('');
      lightingStatusRef.current = 'good';
      lightingMessageRef.current = '';
      return;
    }
    if (Date.now() - lastBackendLightingCheckRef.current <= LIGHTING_CHECK_THROTTLE_MS) {
      return;
    }
    lastBackendLightingCheckRef.current = Date.now();
    const frameDataUrl = canvasEl.toDataURL('image/jpeg', 0.8);
    fetchWithTimeout(`${baseUrl.replace(/\/$/, '')}${SALON_API_PREFIX}/check-lighting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: frameDataUrl }),
      timeoutMs: 8000,
    })
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok) {
          setLightingStatus('good');
          setLightingMessage('');
          lightingStatusRef.current = 'good';
          lightingMessageRef.current = '';
          return;
        }
        const statusMap = { good: 'good', moderate: 'moderate', bad: 'poor' };
        const normalizedStatus = String(d?.status || '').toLowerCase();
        const msg = String(d?.message || '');
        const analysisFailed = /error analyzing lighting/i.test(msg);
        const backendStatus =
          analysisFailed ||
          d?.lighting_ok === true ||
          normalizedStatus === 'good' ||
          normalizedStatus === 'moderate'
            ? 'good'
            : statusMap[normalizedStatus] || 'poor';
        setLightingStatus(backendStatus);
        setLightingMessage(analysisFailed ? '' : msg);
        lightingStatusRef.current = backendStatus;
        lightingMessageRef.current = analysisFailed ? '' : msg;
      })
      .catch(() => {
        setLightingStatus('good');
        setLightingMessage('');
        lightingStatusRef.current = 'good';
        lightingMessageRef.current = '';
      });
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setCameraAwaitingTap(false);
      setCameraError('');
      // Release any leftover native UVC from macro — ExtCam must reclaim USB HD face cam.
      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
      // Drop any wrong live stream (often USB MIC via facingMode:user) before reopen.
      try {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      await new Promise((r) => setTimeout(r, 450));

      let faceCamId = '';
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const cams = await listMicroscopeVideoInputs();
          const pick = pickBuiltinFrontCamera(cams);
          faceCamId = pick?.deviceId || '';
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[SalonScan] cameras', describeVideoInputs(cams), 'picked', faceCamId);
          }
          const stream = await openBuiltinFaceCameraStream({
            deviceId: faceCamId,
            timeoutMs: 12000,
            width: 1280,
            height: 720,
          });
          streamRef.current = stream;
          const videoEl = videoRef.current;
          if (videoEl) {
            videoEl.srcObject = stream;
            await videoEl.play().catch(() => {});
            const applySize = () => {
              if (videoEl.videoWidth && videoEl.videoHeight) {
                setVideoSize({ width: videoEl.videoWidth, height: videoEl.videoHeight });
              }
            };
            if (videoEl.readyState >= 1) applySize();
            else videoEl.onloadedmetadata = applySize;
          }
          setPreviewEnabled(true);
          setLightingStatus('good');
          lightingStatusRef.current = 'good';
          return;
        } catch (err) {
          lastErr = err;
          await new Promise((r) => setTimeout(r, 600 + attempt * 400));
        }
      }
      throw lastErr || new Error('Camera failed');
    } catch (err) {
      setCameraError(String(err?.message || err || 'Camera failed'));
      setPreviewEnabled(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEngineError('');
    setEngineReady(false);
    void warmSalonFaceEngine()
      .then((data) => {
        if (cancelled) return;
        if (data?.ready) setEngineReady(true);
        else setEngineError(data?.message || 'Face engine not ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setEngineError(String(err?.message || err || 'Face engine failed to start'));
      });
    (async () => {
      if (requiresCameraUserGesture() && !(await hasCameraPermissionGranted())) {
        if (!cancelled) setCameraAwaitingTap(true);
        return;
      }
      if (!cancelled) void startCamera();
    })();
    return () => {
      cancelled = true;
      clearAlignmentTimers();
      stopCamera();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [startCamera, stopCamera, clearAlignmentTimers]);

  const retryCameraAndEngine = useCallback(() => {
    setCameraError('');
    setEngineError('');
    setEngineReady(false);
    handingOffRef.current = false;
    hasAutoCapturedRef.current = false;
    setIsIdentifying(false);
    clearAlignmentTimers();
    stopCamera();
    void warmSalonFaceEngine()
      .then((data) => {
        if (data?.ready) setEngineReady(true);
        else setEngineError(data?.message || 'Face engine not ready');
      })
      .catch((err) => {
        setEngineError(String(err?.message || err || 'Face engine failed to start'));
      });
    void startCamera();
  }, [clearAlignmentTimers, startCamera, stopCamera]);

  useEffect(() => {
    if (voiceGuideEnabled && isIOS && previewEnabled && !speechUnlockedRef.current) {
      setShowVoiceHint(true);
    } else {
      setShowVoiceHint(false);
    }
  }, [voiceGuideEnabled, isIOS, previewEnabled]);

  const handleUnlockSpeech = useCallback(() => {
    if (voiceGuideEnabled && !speechUnlockedRef.current && unlockForIOS) {
      speechUnlockedRef.current = true;
      setShowVoiceHint(false);
      unlockForIOS();
    }
  }, [voiceGuideEnabled, unlockForIOS]);

  const toggleVoiceGuide = useCallback(() => {
    setVoiceGuideEnabled((prev) => {
      const next = !prev;
      if (!next && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (next && isIOS && unlockForIOS) {
        speechUnlockedRef.current = true;
        setShowVoiceHint(false);
        unlockForIOS();
      }
      return next;
    });
  }, [isIOS, unlockForIOS]);

  const handoffAfterCapture = useCallback(
    async (blob) => {
      if (handingOffRef.current) return;
      handingOffRef.current = true;
      setIsIdentifying(true);
      clearAlignmentTimers();
      try {
        const data = await recognizeSalonFace(blob);
        const captureUrl = await persistFaceCapture(blob);
        const status = String(data?.status || '').toLowerCase();

        const retryStatuses = new Set([
          'near_match',
          'ambiguous',
          'multiple_faces',
          'low_quality',
          'face_too_small',
          'no_face',
          'blurry_image',
          'under_exposed',
          'over_exposed',
        ]);
        if (retryStatuses.has(status)) {
          handingOffRef.current = false;
          hasAutoCapturedRef.current = false;
          setIsIdentifying(false);
          setFaceGuidance((prev) => ({
            ...prev,
            statusColor: status === 'near_match' || status === 'ambiguous' ? 'orange' : 'red',
            message:
              data?.message ||
              (status === 'near_match'
                ? 'Face looks familiar — hold still and scan again'
                : status === 'ambiguous'
                  ? 'Look straight, one person only — try again'
                  : status === 'multiple_faces'
                    ? 'Only one person in frame — try again'
                    : 'Adjust position and try again'),
          }));
          return;
        }

        if (
          status === 'confirm_match' &&
          Array.isArray(data?.candidates) &&
          data.candidates.length > 0
        ) {
          stopCamera();
          navigate('/nyafaz-ep/salon/welcome', {
            state: {
              confirmCandidates: data.candidates,
              confirmMessage: data.message,
              captureUrl,
              token,
              deviceInfo,
              next: nextPath,
              mode,
              pendingCheckinId: pendingCheckinId || undefined,
              checkinId: pendingCheckinId || undefined,
              checkinCode: pendingCheckinCode || undefined,
              fromCustomerCode: Boolean(pendingCheckinId),
              preselectedServices,
            },
            replace: true,
          });
          return;
        }

        if (status === 'found' && data?.user) {
          const profile = data.profile || data.user.profile || null;
          const snapshot = {
            faceUserId: data.user.id,
            user: data.user,
            profile,
            confidence: data.confidence ?? null,
            mode: 'returning',
            captureUrl,
          };
          persistFaceProfileSnapshot(snapshot);
          persistFacePending(snapshot);
          try {
            setSalonEntrySource('salon_face');
          } catch (_) {
            /* ignore */
          }
          try {
            await linkPendingCheckinIfNeeded(data.user.id, {
              pendingCheckinId,
              name: data.user.name || data.user.full_name,
              mobile: data.user.mobile,
            });
          } catch (_) {
            /* non-blocking */
          }
          stopCamera();
          navigate('/nyafaz-ep/salon/welcome', {
            state: {
              ...snapshot,
              token,
              deviceInfo,
              next: nextPath,
              mode,
              pendingCheckinId: pendingCheckinId || undefined,
              checkinId: pendingCheckinId || undefined,
              checkinCode: pendingCheckinCode || undefined,
              fromCustomerCode: Boolean(pendingCheckinId),
              preselectedServices,
            },
            replace: true,
          });
          return;
        }

        const unknown =
          status === 'unknown' ||
          status === 'not_found' ||
          status === 'no_match' ||
          !data?.user;
        if (unknown) {
          const pending = {
            mode: 'mobile_lookup',
            captureUrl,
            fromFaceUnknown: true,
            registerReason: 'unknown',
            registerMessage: "We didn't recognize you — enter your mobile number to find your profile.",
          };
          persistFacePending(pending);
          stopCamera();
          navigate('/nyafaz-ep/salon/mobile', {
            state: {
              ...pending,
              token,
              deviceInfo,
              next: nextPath,
              mode,
              pendingCheckinId: pendingCheckinId || undefined,
              pendingCheckinCode: pendingCheckinCode || undefined,
              checkinId: pendingCheckinId || undefined,
              checkinCode: pendingCheckinCode || undefined,
              fromCustomerCode: Boolean(pendingCheckinId),
              preselectedServices,
              guestName: location.state?.guestName,
              guestMobile: location.state?.guestMobile,
            },
            replace: true,
          });
          return;
        }
        const pending = {
          mode: 'register',
          predicted: data?.predicted || null,
          captureUrl,
          registerReason: 'enroll',
          registerMessage: data?.message || 'Create your profile to continue.',
        };
        persistFacePending(pending);
        stopCamera();
        navigate('/nyafaz-ep/salon/register', {
          state: {
            ...pending,
            token,
            deviceInfo,
            next: nextPath,
            mode,
            pendingCheckinId: pendingCheckinId || undefined,
            pendingCheckinCode: pendingCheckinCode || undefined,
            preselectedServices,
            guestName: location.state?.guestName,
            guestMobile: location.state?.guestMobile,
          },
          replace: true,
        });
      } catch (err) {
        handingOffRef.current = false;
        hasAutoCapturedRef.current = false;
        setIsIdentifying(false);
        const raw = String(err?.message || '');
        const timedOut = /timed out/i.test(raw);
        setFaceGuidance((prev) => ({
          ...prev,
          statusColor: 'red',
          message: timedOut
            ? 'Identification timed out — hold still and try again'
            : raw || 'Identification failed — try again',
        }));
      }
    },
    [
      clearAlignmentTimers,
      deviceInfo,
      location.state?.guestMobile,
      location.state?.guestName,
      mode,
      navigate,
      nextPath,
      pendingCheckinCode,
      pendingCheckinId,
      preselectedServices,
      stopCamera,
      token,
    ],
  );

  const handleCapture = useCallback(() => {
    if (!previewEnabled || !videoRef.current || !canvasRef.current || isCapturing || handingOffRef.current) {
      return;
    }
    if (lightingStatusRef.current !== 'good' && lightingStatusRef.current !== 'moderate') {
      hasAutoCapturedRef.current = false;
      return;
    }

    setIsCapturing(true);
    const videoEl = videoRef.current;
    const canvasEl = canvasRef.current;

    void (async () => {
      try {
        const blob = await captureIdentityFaceBlob(canvasEl, videoEl, {
          landmarks: latestLandmarksRef.current,
          maxSide: RECOGNIZE_MAX_SIDE,
          quality: RECOGNIZE_JPEG_QUALITY,
          blurPx: 22,
        });
        checkLighting(canvasEl);
        setIsCapturing(false);
        if (!blob) {
          hasAutoCapturedRef.current = false;
          return;
        }
        void handoffAfterCapture(blob);
      } catch (_) {
        setIsCapturing(false);
        hasAutoCapturedRef.current = false;
      }
    })();
  }, [checkLighting, handoffAfterCapture, isCapturing, previewEnabled]);

  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  }, [handleCapture]);

  const handleLandmarksDetected = useCallback(
    (landmarks, videoDimensions) => {
      if (handingOffRef.current || isIdentifying) return;

      if (videoDimensions?.multipleFaces) {
        latestLandmarksRef.current = null;
        faceDetectedRef.current = false;
        setFaceGuidance({
          isInsideFrame: false,
          statusColor: 'red',
          message: 'Only one person in frame',
          isFaceStraight: false,
          isFaceNear: false,
        });
        if (perfectAlignmentStartTimeRef.current) {
          clearAlignmentTimers();
        }
        return;
      }

      const faceDetected = Boolean(landmarks && landmarks.length > 0);
      faceDetectedRef.current = faceDetected;
      latestLandmarksRef.current = faceDetected ? landmarks : null;

      if (landmarks && videoDimensions) {
        if (videoDimensions.width && videoDimensions.height) {
          setVideoSize({ width: videoDimensions.width, height: videoDimensions.height });
        }

        const canvasEl = canvasRef.current;
        const videoEl = videoRef.current;
        if (canvasEl && videoEl?.videoWidth) {
          canvasEl.width = videoEl.videoWidth;
          canvasEl.height = videoEl.videoHeight;
          const ctx = canvasEl.getContext('2d');
          if (ctx) {
            ctx.drawImage(videoEl, 0, 0);
            checkLighting(canvasEl);
          }
        }

        const ovalBounds = calculateGuideBounds(videoDimensions.width, videoDimensions.height);
        const lightingOk =
          lightingStatusRef.current === 'good' || lightingStatusRef.current === 'moderate';
        const lightingMsg = lightingMessageRef.current || '';
        const guidance = calculateFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          ovalBounds,
          lightingOk,
          lightingMsg,
        );
        setFaceGuidance(guidance);

        const qualifiesForCapture =
          guidance.isFaceNear &&
          (guidance.statusColor === 'green' || guidance.isInsideFrame) &&
          guidance.isFaceStraight &&
          faceDetected &&
          lightingOk &&
          engineReady &&
          !engineError &&
          !isCapturing &&
          !hasAutoCapturedRef.current &&
          previewEnabled;

        if (qualifiesForCapture) {
          if (gracePeriodTimerRef.current) {
            clearTimeout(gracePeriodTimerRef.current);
            gracePeriodTimerRef.current = null;
          }

          if (!perfectAlignmentStartTimeRef.current) {
            perfectAlignmentStartTimeRef.current = Date.now();
            setCountdown(Math.ceil(AUTO_CAPTURE_DELAY_MS / 1000));

            if (perfectAlignmentTimerRef.current) {
              clearTimeout(perfectAlignmentTimerRef.current);
            }
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }

            countdownIntervalRef.current = setInterval(() => {
              if (!perfectAlignmentStartTimeRef.current) return;
              const elapsed = Date.now() - perfectAlignmentStartTimeRef.current;
              const remaining = Math.ceil((AUTO_CAPTURE_DELAY_MS - elapsed) / 1000);
              if (remaining > 0) setCountdown(remaining);
              else {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
                setCountdown(0);
              }
            }, 50);

            perfectAlignmentTimerRef.current = setTimeout(() => {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              hasAutoCapturedRef.current = true;
              perfectAlignmentStartTimeRef.current = null;
              perfectAlignmentTimerRef.current = null;
              setCountdown(null);
              if (handleCaptureRef.current) handleCaptureRef.current();
            }, AUTO_CAPTURE_DELAY_MS);
          }
        } else {
          // Soft grace for brief wobble / yellow flicker — don't instantly cancel
          setCountdown(null);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (perfectAlignmentStartTimeRef.current && !gracePeriodTimerRef.current) {
            gracePeriodTimerRef.current = setTimeout(() => {
              perfectAlignmentStartTimeRef.current = null;
              gracePeriodTimerRef.current = null;
              if (perfectAlignmentTimerRef.current) {
                clearTimeout(perfectAlignmentTimerRef.current);
                perfectAlignmentTimerRef.current = null;
              }
            }, GRACE_PERIOD_MS);
          }
        }
      } else {
        latestLandmarksRef.current = null;
        setFaceGuidance({
          isInsideFrame: false,
          statusColor: 'red',
          message: 'Position your face',
          isFaceStraight: false,
          isFaceNear: false,
        });
        if (perfectAlignmentStartTimeRef.current) {
          clearAlignmentTimers();
        }
      }
    },
    [checkLighting, clearAlignmentTimers, engineError, engineReady, isCapturing, isIdentifying, previewEnabled],
  );

  return (
    <div className="face-identity-page face-identity-page--scan face-detection-page page-bg fi-lux-theme salon-scan-page">
      <div className="face-detection-page-nav face-identity-capture-nav">
        <FaceIdentityBrandLogo
          showLogo={showLogo}
          orgLogoUrl={orgLogoUrl}
          onLogoError={onLogoError}
          className="face-identity-capture-brand"
          imgClassName="fi-brand-logo__img fi-brand-logo__img--on-camera"
          fallbackClassName="fi-brand-logo__fallback fi-brand-logo__fallback--on-camera face-identity-brand face-identity-brand--on-camera"
        />
        <div className="face-identity-capture-nav-actions">
          <button
            type="button"
            className={`face-identity-voice-toggle${voiceGuideEnabled ? ' is-on' : ''}`}
            onClick={toggleVoiceGuide}
            aria-pressed={voiceGuideEnabled}
          >
            Voice guide {voiceGuideEnabled ? 'on' : 'off'}
          </button>
          <FaceIdentityFlowNav
            showBack={false}
            onHome={() => {
              clearAlignmentTimers();
              stopCamera();
              navigate(resolveSalonHomePath(), { replace: true });
            }}
            homePath={resolveSalonHomePath()}
          />
        </div>
      </div>

      <main className="face-detection-page-main" onClick={handleUnlockSpeech} role="presentation">
        <video
          ref={videoRef}
          playsInline
          muted
          className={`video-preview${previewEnabled ? '' : ' video-preview--preparing'}`}
        />
        {!previewEnabled ? <CameraPrepPlaceholder variant="dark" message="Starting camera…" /> : null}
        <canvas ref={canvasRef} className="capture-canvas" />

        {voiceGuideEnabled && isIOS && previewEnabled && showVoiceHint && (
          <button
            type="button"
            className="face-detection-voice-enable-btn face-identity-voice-enable-btn"
            onClick={handleUnlockSpeech}
            aria-label="Enable voice guide sound"
          >
            Tap to enable voice guide sound
          </button>
        )}

        {previewEnabled && (
          <FaceIdentityScanOverlay
            statusColor={isIdentifying ? 'green' : faceGuidance.statusColor}
            message={faceGuidance.message}
            videoWidth={videoSize.width}
            videoHeight={videoSize.height}
            showOverlay
            countdown={isIdentifying ? null : countdown}
            lightingOk={lightingPass}
            faceDetected={faceDetectedRef.current || isIdentifying}
            faceStraight={faceGuidance.isFaceStraight || isIdentifying}
            scanning={isIdentifying}
            engineReady={engineReady && !engineError}
          />
        )}

        {previewEnabled && !isIdentifying && (
          <FaceProcessor
            videoElement={videoRef.current}
            onLandmarksDetected={handleLandmarksDetected}
            enabled={previewEnabled && !isIdentifying}
          />
        )}

        {cameraAwaitingTap && !previewEnabled && !cameraError && !engineError && (
          <div className="face-identity-identifying-overlay">
            <p>Tap to allow the tablet camera.</p>
            <div className="face-identity-identifying-overlay__actions">
              <button
                type="button"
                className="face-identity-identifying-overlay__btn face-identity-identifying-overlay__btn--primary"
                onClick={() => void startCamera()}
              >
                Start camera
              </button>
            </div>
          </div>
        )}

        {(cameraError || engineError) && (
          <div className="face-identity-identifying-overlay face-identity-identifying-overlay--error">
            <p>{engineError || cameraError}</p>
            <div className="face-identity-identifying-overlay__actions">
              <button
                type="button"
                className="face-identity-identifying-overlay__btn face-identity-identifying-overlay__btn--primary"
                onClick={retryCameraAndEngine}
              >
                Try again
              </button>
              <button
                type="button"
                className="face-identity-identifying-overlay__btn"
                onClick={() => {
                  clearAlignmentTimers();
                  stopCamera();
                  navigate(resolveSalonHomePath(), { replace: true });
                }}
              >
                Home
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SalonScanPage;
